/**
 * Usage tab — per-day material-token size over a rolling last-14-day window
 * (from `useUsage(from, to)`), rendered as token-based CSS bars plus a per-pass
 * breakdown table. No charting dependency: the bars are plain <span>s sized by
 * percent-of-peak, coloured with the shared saga-design tokens.
 *
 * "Material tokens" are the measured input/output token counts a routine pass
 * consumed (the record path measures them fail-open, so a pass may be unmeasured
 * — null — e.g. before the Anthropic key exists; those render as "—").
 *
 * Loading/error/empty states mirror RunsTab. The window bounds are computed
 * client-side from the browser `Date` (this is client code, not a workflow
 * script, so `new Date()` is fine here); the bucketing/formatting helpers are
 * exported pure so they can be unit-tested without a DOM.
 */

import { useMemo } from 'react';
import WatchtowerCard from '../components/WatchtowerCard';
import { useUsage, type UsageDay } from '../lib/queries';

// ---------------------------------------------------------------------------
// Pure helpers (exported for unit tests — no DOM needed).
// ---------------------------------------------------------------------------

/** Inclusive ISO date `days` back from `end` (default today), as YYYY-MM-DD. */
export function isoDaysAgo(days: number, end = new Date()): string {
  const d = new Date(end);
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

/** The largest per-day total (input + output) across the window; 0 if empty. */
export function maxTotal(days: UsageDay[]): number {
  return days.reduce((m, d) => Math.max(m, d.total_input + d.total_output), 0);
}

// ---------------------------------------------------------------------------
// The tab.
// ---------------------------------------------------------------------------

export default function UsageTab() {
  const to = useMemo(() => isoDaysAgo(0), []);
  const from = useMemo(() => isoDaysAgo(14), []);
  const { data, isLoading, isError, error } = useUsage(from, to);

  // The Watchtower card is independent of the usage query (it's how a user
  // authorizes REAL usage/cost reporting in the first place), so it renders
  // above every state of the tab — including the loading/error/empty ones.
  const watchtower = <WatchtowerCard />;

  if (isLoading) {
    return (
      <section className="usage-tab" aria-label="Usage">
        {watchtower}
        <p className="usage-loading note">Loading usage…</p>
      </section>
    );
  }
  if (isError) {
    return (
      <section className="usage-tab" aria-label="Usage">
        {watchtower}
        <p className="usage-error error" role="alert">
          Failed to load usage: {error instanceof Error ? error.message : 'unknown error'}
        </p>
      </section>
    );
  }

  const days = data?.days ?? [];
  if (days.length === 0) {
    return (
      <section className="usage-tab" aria-label="Usage">
        {watchtower}
        <p className="usage-empty note">No usage recorded in this window.</p>
      </section>
    );
  }
  const peak = maxTotal(days) || 1;

  return (
    <section className="usage-tab" aria-label="Usage">
      {watchtower}
      <h2 className="section-title">Material token size (last 14 days)</h2>
      <div className="card">
        {days.map((d) => {
          const total = d.total_input + d.total_output;
          const pct = Math.round((total / peak) * 100);
          return (
            <div key={d.date} className="usage-day-row">
              <span className="usage-day-date">{d.date}</span>
              <span
                className="usage-day-bar"
                style={{
                  display: 'inline-block',
                  width: `${pct}%`,
                  minWidth: '2px',
                  height: '0.9rem',
                  background: 'var(--good)',
                  borderRadius: 'var(--radius)',
                }}
                aria-hidden="true"
              />
              <span className="usage-day-total detail-num">
                {total.toLocaleString()} ({d.total_input.toLocaleString()} in /{' '}
                {d.total_output.toLocaleString()} out)
              </span>
            </div>
          );
        })}
      </div>

      <h3 className="section-title">Passes</h3>
      <table className="detail-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Kind</th>
            <th>Run</th>
            <th>Input</th>
            <th>Output</th>
          </tr>
        </thead>
        <tbody>
          {days.flatMap((d) =>
            d.passes.map((p) => (
              <tr key={`${d.date}-${p.run_id}`}>
                <td>{d.date}</td>
                <td>{p.run_kind}</td>
                <td>{p.run_id}</td>
                <td className="detail-num">
                  {p.input_material_tokens?.toLocaleString() ?? '—'}
                </td>
                <td className="detail-num">
                  {p.output_material_tokens?.toLocaleString() ?? '—'}
                </td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    </section>
  );
}
